import React, { useEffect, useMemo } from 'react'
import { useTheme } from 'styled-components'

import {
  usePRFileAllFrames,
  useIsCameraMoving,
  useRootNodeAbsoluteExtent,
  PRMarinaLayerType,
  usePRFileSections,
  convertEmbindVectorToArray,
  useComparePatchBounds,
} from '@sketch-hq/sketch-web-renderer'

import {
  EntityFooterLabel,
  EntityOverlay,
  getSectionOverlayEntities,
  isUnstyledSection,
  OverlayEntity,
  useEntityOverlayProps,
  EntityInteractions,
  EntityOverlayMode,
  toEntityOverlayMode,
  useCanvasInteractions,
} from '@sketch/canvas'
// Deep path, not the `shares/FrameView` barrel: it re-exports FrameView, which
// mounts CanvasSurface, which renders the canvas this overlays - and the cycle
// breaks the bundler. `useEntityOverlaysState` reaches the inspector the same
// way for the same reason.
import { useInspectorContext } from '../../../FrameView/hooks/inspector'
import {
  useEntityOverlaysState,
  useEntityKeyboardShortcuts,
} from '../../../../canvas/hooks'
import { Rect, isVariantSetNode } from '@sketch/inspector'
import { useCanvasAnnotations } from '../../../../annotations/hooks'
import {
  canSplitEntity,
  CompareMode,
  COMPARE_TINT,
  EntityCompareActions,
  describeOnlyIn,
  markedEntityUUIDs,
  SplitViewSlider,
  EntityChange,
  useCompare,
  useOpenChangedEntity,
} from '../../../../versioning/compare'

/** An overlay entity, or one drawn from another version with plain bounds. */
type CanvasOverlayEntity = OmitSafe<OverlayEntity, 'bounds'> & { bounds: Rect }

type PageCanvasEntityOverlayProps = {
  entity: CanvasOverlayEntity
  mode: EntityOverlayMode
  framesRefs: React.RefObject<Record<string, HTMLDivElement | null>>
  isSelected: boolean
  isHovered: boolean
  interactions: EntityInteractions
  rootExtent: Rect
  // In the single-Section focus view, render the overlay even when the entity's
  // layer is hidden (isEnabled() === false), so a focused Section's children
  // stay navigable regardless of their design-time visibility.
  forceShow?: boolean
  /** What a running comparison found changed about it. */
  change?: EntityChange
  /** Drawn from the other version of the page: it has no node on this canvas. */
  isAbsent?: boolean
  /** Whether a change elsewhere is the one being read, so this one steps back. */
  isDimmed?: boolean
  /** Said about it under its bounds: its caption, its actions, or both. */
  footer?: React.ReactNode
  /** Whether two versions are being compared. */
  isComparing?: boolean
}

const PageCanvasEntityOverlay = React.memo(function PageCanvasEntityOverlay({
  entity,
  mode,
  framesRefs,
  isSelected,
  isHovered,
  interactions,
  rootExtent,
  forceShow,
  change,
  isAbsent,
  isDimmed,
  footer,
  isComparing,
}: PageCanvasEntityOverlayProps) {
  const theme = useTheme()
  const node = entity.getNode()

  const isHidden = node?.isEnabled() === false
  const isSection = node ? node.isSection() : change?.entityKind === 'section'

  // A section's `bounds` field reflects the extent including any overflowing
  // children, which can diverge from the section's own frame as shown in the
  // Mac app. getAbsoluteLayerBounds() reports the section's own frame instead.
  const bounds =
    isSection && node ? node.getAbsoluteLayerBounds() : entity.bounds

  const overlayProps = useEntityOverlayProps(entity, {
    kind: isSection ? 'section' : 'frame',
    mode,
    isSelected,
    isHovered,
    interactions,
  })

  // Hidden entities don't render overlay decorations unless selected - or unless
  // explicitly forced (a focused Section's children must stay overlaid even when
  // their layer is hidden, so they remain navigable).
  if (isHidden && !isSelected && !forceShow) {
    return null
  }

  // A variant set is a Section, not a component - it gets the component accent
  // colour of its own accord rather than by being counted as one.
  const isVariantSet = isVariantSetNode(node)

  const isComponent =
    node?.getLayer()?.type.value === PRMarinaLayerType.SymbolMaster

  const variantProperties = node
    ? convertEmbindVectorToArray(node.getVariantConfiguration()).map(
        ({ propertyName, valueName }) => ({ propertyName, valueName })
      )
    : undefined

  return (
    <EntityOverlay
      ref={(ref: HTMLDivElement | null) => {
        if (framesRefs.current) {
          framesRefs.current[entity.objectId] = ref
        }
      }}
      bounds={bounds}
      artboardName={entity.name}
      artboardUUID={entity.objectId}
      isComponent={isComponent}
      isVariantSet={isVariantSet}
      variantProperties={variantProperties}
      // A Section with no fill or border of its own is invisible on the canvas -
      // the dashed outline stands in for it, and steps aside as soon as the
      // Section gets a style that paints its own boundary.
      showDashedOutline={isUnstyledSection(node)}
      rootExtent={rootExtent}
      accentColor={isAbsent ? COMPARE_TINT.strong : undefined}
      // Pink marks the compared version everywhere else; the caption below
      // says which version has it, so the rest need only one colour
      outlineColor={
        change && (isAbsent ? COMPARE_TINT.strong : theme.colors.sketch.A)
      }
      outlineDimmed={isDimmed}
      footer={footer}
      // Selecting is how a change is read while comparing, so Enter must not
      // leave the comparison for the entity's own view
      disableOpenOnEnter={isComparing}
      {...overlayProps}
    />
  )
})

/** Keeps the first entity seen for each id, so overlay keys stay unique. */
const dedupeByObjectId = (entities: OverlayEntity[]) => {
  const byId = new Map<string, OverlayEntity>()

  for (const entity of entities) {
    if (!byId.has(entity.objectId)) byId.set(entity.objectId, entity)
  }

  return [...byId.values()]
}

/**
 * The overlays on the page canvas's frames and Sections: their names, CTA and
 * outline, and the interactions on them. While comparing versions, changed
 * entities are outlined, and the frames and Sections the canvas draws from the
 * other version get overlays too.
 * Note: we use a unified component for both view modes to use only one React tree and
 * avoid remounting all overlays when switching modes.
 */
export const PageCanvasEntityOverlays = React.memo(
  function PageCanvasEntityOverlays({
    focusedSectionUUID,
    canvasRef,
    compareMode,
    canSwap,
    onToggleCompareMode,
  }: {
    focusedSectionUUID?: string
    /** The canvas these sit on, which a click has to land in to deselect. */
    canvasRef: React.RefObject<HTMLElement | null>
    /** The one compare mode on screen, and the actions that ask for it. */
    compareMode: CompareMode
    /** Whether the selection can be swapped - `useCompareMode` decides it. */
    canSwap: boolean
    onToggleCompareMode?: (mode: CompareMode) => void
  }) {
    const { isInspectorActive } = useInspectorContext()
    const fileFrames = usePRFileAllFrames()
    const fileSections = usePRFileSections()
    const sections = fileSections ?? []

    // What the renderer already means by a top-level frame: one whose chain of
    // ancestor Sections terminates on the page. A frame held by anything else -
    // a group, or another frame - is that thing's content, and the API does not
    // know it as a frame of the page at all, so opening it leads nowhere: it
    // answers "Frame not found", which the frame view reads as a broken link
    // and redirects away from, landing on the document's first page.
    const pageFrames = useMemo(
      () =>
        (fileFrames ?? []).filter(frame => frame.getNode()?.isTopLevelFrame()),
      [fileFrames]
    )

    const isFocusingSection = focusedSectionUUID !== undefined

    // In the single-Section focus view, the overlays come from the focused
    // Section's own subtree: its direct child Sections to drill into, plus every
    // frame it contains at any depth. On the normal page canvas the PRFile's
    // top-level entities are used instead.
    // Memoized: this walks the Section's subtree, and the component re-renders
    // on every camera move to reposition the overlays.
    const focusedSectionEntities = useMemo(
      () =>
        isFocusingSection
          ? getSectionOverlayEntities(
              fileSections
                ?.find(section => section.objectId === focusedSectionUUID)
                ?.getNode() ?? null
            )
          : [],
      [isFocusingSection, focusedSectionUUID, fileSections]
    )

    // Paint larger (outer) sections first so nested (smaller) sections land on
    // top and stay clickable - otherwise a parent section's overlay would cover
    // its children and you couldn't navigate into them from the page canvas.
    const byContainment = (a: OverlayEntity, b: OverlayEntity) =>
      b.bounds.width * b.bounds.height - a.bounds.width * a.bounds.height

    // A variant set is reported by the renderer as both a Section and a frame,
    // so the two lists can name the same entity and React would see the same
    // key twice. Sections are listed first and win: drilling into one is what
    // its overlay is for.
    const decoratedEntities: OverlayEntity[] = dedupeByObjectId(
      isFocusingSection
        ? [...focusedSectionEntities].sort(byContainment)
        : [...[...sections].sort(byContainment), ...pageFrames]
    )

    // Resolved once for the whole list rather than per entity - see
    // `useEntityOverlaysState`.
    const {
      framesRefs,
      selectedEntityUUID,
      isLayerSelected,
      clearSelection,
      hoveredEntityUUID,
      interactions,
    } = useEntityOverlaysState()

    const rootExtent = useRootNodeAbsoluteExtent()

    const compare = useCompare()
    const changeByUUID = compare?.entityByUUID
    const changes = compare?.entities
    const absentChanges = compare?.absentEntities
    const patchBounds = useComparePatchBounds()
    const openChangedEntity = useOpenChangedEntity()

    /**
     * The entities a comparison names, with their parents, or null when none is
     * running. Gated on the result, not on `entityByUUID`: that is empty while
     * one is still being read, which would take every overlay off the page.
     *
     * Parents are kept because a Section is only drilled into through its own
     * overlay, which the frames inside it need.
     */
    const comparedUUIDs = useMemo(
      () => (changes ? markedEntityUUIDs(changes) : null),
      [changes]
    )

    // The older version is drawn on the left, so the other one follows the side
    const isViewingNewer = compare?.versions?.isViewingNewer ?? true
    const isComparing = Boolean(compare?.versions)

    /**
     * What sits under an entity: which version has it, when only one does, and
     * what can be asked of it while it is the selection. Nothing for a selected
     * layer, which has its own outline to sit under - under the frame around it
     * the actions would read as the frame's.
     */
    const renderFooter = (uuid: string, isSelected: boolean) => {
      const change = changeByUUID?.get(uuid)
      if (!change) return undefined

      const caption =
        change.kind !== 'modified' ? describeOnlyIn(change.kind) : undefined

      // Swap is offered wherever Split is, so it decides whether there are any
      const actions = isSelected && canSwap

      if (!caption && !actions) return undefined

      return (
        <>
          {caption && <EntityFooterLabel>{caption}</EntityFooterLabel>}
          {actions && (
            <EntityCompareActions
              canSplit={canSplitEntity(change, compare?.layers ?? [])}
              mode={compareMode}
              sourceOnRight={!isViewingNewer}
              onToggleMode={mode => onToggleCompareMode?.(mode)}
            />
          )}
        </>
      )
    }

    /*
    While a change is being read, the marks on the others step back. They all
    say "this version has it" in the same two colours, so at full strength the
    one the reader picked is just another of them - and a frame outlined a few
    points outside its own selected layer reads as one selection drawn twice.
    */
    const readingAChange = Boolean(selectedEntityUUID) || isLayerSelected

    // Dimming alone still let an unchanged frame be selected and opened
    const overlaidEntities = comparedUUIDs
      ? decoratedEntities.filter(entity => comparedUUIDs.has(entity.objectId))
      : decoratedEntities

    // Placed where the renderer draws them; left out until it has placed them
    const absentEntities = useMemo(
      () =>
        (absentChanges ?? []).flatMap<CanvasOverlayEntity>(change => {
          const bounds = patchBounds.get(change.uuid)
          if (!bounds?.width || !bounds.height) return []

          const { x, y, width, height } = bounds
          return [
            {
              objectId: change.uuid,
              name: change.name,
              bounds: { x, y, width, height },
              getNode: () => null,
            },
          ]
        }),
      [absentChanges, patchBounds]
    )

    // Opening one goes to the version that has it: this one lacks it
    const absentInteractions = useMemo<EntityInteractions>(
      () => ({
        ...interactions,
        navigateToEntity: (uuid, _kind, actionType) => {
          const change = changeByUUID?.get(uuid)
          if (change) openChangedEntity(change, actionType)
        },
      }),
      [interactions, changeByUUID, openChangedEntity]
    )
    // The renderer reports this as nullable before it is ready.
    const isCameraMoving = useIsCameraMoving() ?? false
    const { isPlacingDraftAnnotation } = useCanvasAnnotations()
    const { isEnabled: isCanvasInteractionsEnabled } = useCanvasInteractions()

    const mode = toEntityOverlayMode({
      isCanvasInteractionsEnabled,
      isInspectorActive,
      isPlacingDraftAnnotation: Boolean(isPlacingDraftAnnotation),
    })

    useEntityKeyboardShortcuts({
      mode,
      selectedEntityUUID,
      clearSelection,
      framesRefs,
      canvasRef,
      isCameraMoving,
    })

    // Its overlay is gone, so nothing is left to show the selection or clear it
    useEffect(() => {
      if (!comparedUUIDs || !selectedEntityUUID) return
      if (!comparedUUIDs.has(selectedEntityUUID)) clearSelection()
    }, [comparedUUIDs, selectedEntityUUID, clearSelection])

    return (
      <>
        {overlaidEntities.map(entity => (
          <PageCanvasEntityOverlay
            key={entity.objectId}
            entity={entity}
            mode={mode}
            framesRefs={framesRefs}
            isSelected={selectedEntityUUID === entity.objectId}
            isHovered={hoveredEntityUUID === entity.objectId}
            interactions={interactions}
            rootExtent={rootExtent}
            forceShow={isFocusingSection}
            isDimmed={readingAChange && selectedEntityUUID !== entity.objectId}
            change={changeByUUID?.get(entity.objectId)}
            isComparing={isComparing}
            footer={
              comparedUUIDs
                ? renderFooter(
                    entity.objectId,
                    selectedEntityUUID === entity.objectId
                  )
                : undefined
            }
          />
        ))}

        {absentEntities.map(entity => (
          <PageCanvasEntityOverlay
            key={entity.objectId}
            entity={entity}
            mode={mode}
            framesRefs={framesRefs}
            isSelected={selectedEntityUUID === entity.objectId}
            isHovered={hoveredEntityUUID === entity.objectId}
            interactions={absentInteractions}
            rootExtent={rootExtent}
            isDimmed={readingAChange && selectedEntityUUID !== entity.objectId}
            change={changeByUUID?.get(entity.objectId)}
            isAbsent
            isComparing
            // These are only ever drawn while comparing, and the caption is the
            // only thing saying which version has them
            footer={renderFooter(
              entity.objectId,
              selectedEntityUUID === entity.objectId
            )}
          />
        ))}

        {comparedUUIDs && compareMode === 'split' && selectedEntityUUID && (
          <SplitViewSlider
            key={selectedEntityUUID}
            entityUUID={selectedEntityUUID}
            isViewingNewer={isViewingNewer}
            rootExtent={rootExtent}
          />
        )}
      </>
    )
  }
)
